import * as THREE from 'three';

// Manifest + on-demand image loading for visualizers/gallery.js. Built offline
// by `npm run build:assets` (tools/build-gallery.mjs).
//
// The manifest is preloaded at application startup, same preload/get shape as
// paper-texture.js and soundboard-texture.js - it is 30KB of numbers and
// gallery.js needs it before it can choose anything. The IMAGES are the exact
// opposite and are never preloaded: one decoded photograph is 5-27MB of GPU
// memory depending on tier, so exactly one is resident at a time, plus one
// more for the length of a crossfade. See gallery.js's own disposal.
const MANIFEST_URL = 'gallery/gallery.json';

// Index = quality tier -> which of the manifest's variants to ask for. A
// decoded 4K texture is over 30MB and would not survive on a Pi; 1400 decodes
// to about 5MB, which is the whole reason the build script emits three sizes.
const TIER_WIDTH = [1400, 2200, 3200];

let _manifest = null;
let _promise = null;

export function preloadGalleryManifest() {
  if (_promise) return _promise;
  _promise = fetch(MANIFEST_URL)
    .then(function (r) {
      if (!r.ok) throw new Error('gallery.json HTTP ' + r.status);
      return r.json();
    })
    .then(function (manifest) {
      if (!manifest.images || manifest.images.length === 0) throw new Error('gallery.json has no images');
      _manifest = manifest;
      return _manifest;
    })
    .catch(function (err) {
      console.error('[party-mode] gallery manifest unavailable, Gallery will not be offered', err);
      _manifest = null;
      return null;
    });
  return _promise;
}

export function getGalleryManifest() {
  return _manifest;
}

// Resolves to a configured THREE.Texture, or rejects. The caller owns the
// texture from here and must dispose() it - nothing is cached, deliberately:
// a cache is the one thing that would quietly put ten photographs in memory.
export function loadGalleryTexture(entry, tier) {
  const width = TIER_WIDTH[tier] != null ? TIER_WIDTH[tier] : TIER_WIDTH[1];
  const variant = entry.variants[String(width)] || entry.variants[String(TIER_WIDTH[0])];
  if (!variant) return Promise.reject(new Error('no variant for ' + entry.id));
  const url = 'gallery/' + variant.file;
  return new Promise(function (resolve, reject) {
    new THREE.TextureLoader().load(url, function (texture) {
      texture.colorSpace = THREE.SRGBColorSpace;
      // The sampled window is a little smaller than the variant (see
      // gallery.js's OVERSCALE), so at tier 2 a 3200px image lands on an
      // ~1800px buffer minified about 1.4x. Without mipmaps that aliases on
      // foliage and brickwork, which on a still photograph is the most
      // visible flaw available. Costs a third more memory, which the 1400
      // variant can afford and the 3200 variant is only ever asked for off
      // a Pi.
      texture.generateMipmaps = true;
      texture.minFilter = THREE.LinearMipmapLinearFilter;
      texture.magFilter = THREE.LinearFilter;
      // Belt and braces against a drift that escapes its clamp: an edge
      // repeated is a stretched pixel, an edge wrapped is the far side of the
      // photograph appearing in frame.
      texture.wrapS = THREE.ClampToEdgeWrapping;
      texture.wrapT = THREE.ClampToEdgeWrapping;
      texture.anisotropy = 4;
      texture.needsUpdate = true;
      resolve({ texture: texture, variant: variant });
    }, undefined, function () {
      reject(new Error(url + ' failed to load'));
    });
  });
}

export { TIER_WIDTH };
